'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Button, LoadingDark, Padding, TabText, TabTextItem, Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { updateCurationsOrderService } from '@musinsa/services-curator';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useCuratorBroadcastPublisher } from '../../common';
import { BottomButtonWrapper } from '../../common/components/BottomButtonWrapper';
import { useCuratorSnackbar } from '../../common/hooks';
import { useNavigate } from '../../common/hooks/useNavigate';
import { curatorQueries } from '../../query-factory';
import { curationsFactory } from '../../query-factory/curations.factory';
import { VirtualizedDraggableCurationList } from './VirtualizedDraggableCurationList';

type CurationTabType = 'GOODS' | 'SNAP2' | undefined;

interface Props {
  className?: string;
}

export const CurationEditOrderList = ({ className }: Props) => {
  const { navigateBack } = useNavigate();
  const snackbar = useCuratorSnackbar();
  const { isWebview, service } = useDeviceInfo();
  const { triggerQuerySync } = useCuratorBroadcastPublisher();
  const [orderedIds, setOrderedIds] = useState<(number | string)[]>([]);
  const [tabType, setTabType] = useState<CurationTabType>(undefined);

  const isMusinsa = service === SERVICE.MUSINSA;

  const userInfoQuery = useQuery({
    ...curatorQueries.profile.userInfo,
  });
  const snsTitle = userInfoQuery.data?.snsTitle ?? '';

  const tabsVisibilityQuery = useQuery({
    ...curatorQueries.curations.curationTabsVisibility({ snsTitle }),
    enabled: isMusinsa && Boolean(snsTitle),
  });
  const showTabs = isMusinsa && tabsVisibilityQuery.data?.isVisible === true;

  const { mutate: updateCurationsOrder } = useMutation({
    mutationFn: updateCurationsOrderService,
    onSuccess: (_, variables) => {
      if (isMusinsa && snsTitle) {
        amplitudeSendEvent({
          action_name: 'click',
          button_name: 'curation_edit_complete',
          component_name: 'button',
          event_name: 'click_button',
          from: getScreenName(),
          id: snsTitle,
          name: 'order_change',
          section_name: 'curator_curationfeed',
          type: variables.type === 'GOODS' ? 'goods' : 'snap',
        });
      }
      triggerQuerySync({
        queryKeys: [curatorQueries.curations.orderList._def, curatorQueries.curations.myList._def],
      });
      snackbar.success('순서가 변경되었습니다.');
      navigateBack();
    },
    onError: () => {
      snackbar.error('순서 변경에 실패했습니다.');
    },
  });

  const queryType = showTabs ? tabType : undefined;
  const { isPending, data, isError } = useQuery(
    curationsFactory.orderList(queryType ? { type: queryType } : undefined),
  );

  const handleOrderChange = useCallback((newOrderedIds: (number | string)[]) => {
    setOrderedIds(newOrderedIds);
  }, []);

  const handleTabChange = useCallback((next: CurationTabType) => {
    setTabType(next);
    setOrderedIds([]);
  }, []);

  const handleSubmit = useCallback(() => {
    const idsToSubmit =
      orderedIds.length > 0 ? orderedIds : (data?.curations.map((c) => c.curationId) ?? []);
    updateCurationsOrder({
      curationIds: idsToSubmit,
      ...(queryType ? { type: queryType } : {}),
    });
  }, [orderedIds, data?.curations, queryType, updateCurationsOrder]);

  if (isPending) {
    return isWebview ? null : (
      <div className="flex flex-1 items-center justify-center p-4 grow">
        <LoadingDark size="size24x24" />
      </div>
    );
  }

  if (isError) {
    return null;
  }

  return (
    <div className={twMerge('flex flex-col h-full justify-between grow', className)}>
      {showTabs && (
        <TabText
          colorScheme="gray"
          depth="oneDepth"
          divider="on"
          className="bg-neutral-color-white"
        >
          <TabTextItem
            isSelected={tabType === undefined}
            disabledUnderline={false}
            onClick={() => handleTabChange(undefined)}
          >
            전체
          </TabTextItem>
          <TabTextItem
            isSelected={tabType === 'GOODS'}
            disabledUnderline={false}
            onClick={() => handleTabChange('GOODS')}
          >
            상품
          </TabTextItem>
          <TabTextItem
            isSelected={tabType === 'SNAP2'}
            disabledUnderline={false}
            onClick={() => handleTabChange('SNAP2')}
          >
            스냅
          </TabTextItem>
        </TabText>
      )}

      <VirtualizedDraggableCurationList
        className="pb-69"
        curations={data.curations}
        onOrderChange={handleOrderChange}
      />

      <BottomButtonWrapper>
        <Padding y="3">
          <Button onClick={handleSubmit} variant="primary" size="44" className="w-full">
            <Typography variant="body14pxReg">변경하기</Typography>
          </Button>
        </Padding>
      </BottomButtonWrapper>
    </div>
  );
};
